import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dropp } from '@dropp.cc/payment-sdk';
import droppSdkPackageJson from '@dropp.cc/payment-sdk/package.json';
import './App.css';

const SDK_INIT_STORAGE_KEY = 'dropp_sample_sdk_init_params_v1';
const DASHBOARD_MODULES_STORAGE_KEY = 'dropp_sample_dashboard_modules_v1';
const DROPP_SDK_VERSION = droppSdkPackageJson?.version || 'unknown';

const DASHBOARD_MODULE_OPTIONS = [
  { key: 'funding', label: 'Funding', type: 'Section' },
  { key: 'accounts', label: 'Accounts', type: 'Section' },
  { key: 'merchants', label: 'Merchants', type: 'Section' },
  { key: 'settings', label: 'Settings', type: 'Section' },
  { key: 'support', label: 'Support', type: 'Section' },
  { key: 'fundnow', label: 'Add Cash', type: 'Item' },
  { key: 'linkbank', label: 'Link Bank', type: 'Item' },
  { key: 'linkcard', label: 'Link Card', type: 'Item' },
  { key: 'redeemnow', label: 'Cash Out', type: 'Item' },
  { key: 'fundcrypto', label: 'Fund Crypto', type: 'Item' },
  { key: 'transferusdc', label: 'Transfer USDC', type: 'Item' },
  { key: 'usdchistory', label: 'USDC History', type: 'Item' },
  { key: 'redeemcrypto', label: 'Redeem Crypto', type: 'Item' },
  { key: 'manageaccounts', label: 'Currency Accounts', type: 'Item' },
  { key: 'transactions', label: 'Transactions', type: 'Item' },
  { key: 'merchantlist', label: 'Merchant List', type: 'Item' },
  { key: 'favorites', label: 'Favorites', type: 'Item' },
  { key: 'offers', label: 'Offers', type: 'Item' },
  { key: 'profile', label: 'Profile', type: 'Item' },
  { key: 'pinchange', label: 'Change PIN', type: 'Item' },
  // { key: 'unlinkaccount', label: 'Unlink Account', type: 'Item' },
  // { key: 'accountsettings', label: 'Account Settings', type: 'Item' },
  { key: 'aboutus', label: 'About', type: 'Item' }
];

const DASHBOARD_MODULE_LABELS = DASHBOARD_MODULE_OPTIONS.reduce((acc, module) => {
  acc[module.key] = module.label;
  return acc;
}, {});

const DASHBOARD_SECTION_DEFINITIONS = [
  {
    key: 'funding',
    label: 'Funding',
    items: ['fundnow', 'linkbank', 'linkcard', 'redeemnow', 'fundcrypto', 'transferusdc', 'usdchistory', 'redeemcrypto']
  },
  {
    key: 'accounts',
    label: 'Accounts & transaction',
    items: ['manageaccounts', 'transactions']
  },
  {
    key: 'merchants',
    label: 'Dropp Merchants',
    items: ['merchantlist', 'favorites', 'offers']
  },
  {
    key: 'settings',
    label: 'Settings',
    items: ['profile', 'pinchange'] //, 'unlinkaccount', 'accountsettings']
  },
  {
    key: 'support',
    label: 'Support',
    items: ['aboutus']
  }
];

const DASHBOARD_SECTION_ITEMS_MAP = DASHBOARD_SECTION_DEFINITIONS.reduce((acc, section) => {
  acc[section.key] = section.items;
  return acc;
}, {});

const DEFAULT_DASHBOARD_MODULE_KEYS = DASHBOARD_MODULE_OPTIONS.map((module) => module.key);
const DASHBOARD_ITEM_MODULE_KEYS = DASHBOARD_MODULE_OPTIONS
  .filter((module) => module.type === 'Item')
  .map((module) => module.key);

const ACCOUNT_FAILED_FALLBACK_MESSAGE = 'Account request failed';
const PAGE_FAILED_FALLBACK_MESSAGE = 'Page request failed';
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const toDatetimeLocalValue = (isoString) => {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const fromDatetimeLocalValue = (localValue) => {
  if (!localValue) return '';
  const date = new Date(localValue);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString();
};

const defaultRecurringExpiryIso = () =>
  new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString();

const RECURRING_FREQUENCIES = [
  { label: 'None', value: 'NONE' },
  { label: 'Half Hourly', value: 'HALF_HOURLY' },
  { label: 'Hourly', value: 'HOURLY' },
  { label: 'Daily', value: 'DAILY' },
  { label: 'Weekly', value: 'WEEKLY' },
  { label: 'Monthly', value: 'MONTHLY' },
  { label: 'Yearly', value: 'YEARLY' }
];

const ENVIRONMENTS = [
  { label: 'Sandbox', value: 'sandbox' },
  { label: 'Production', value: 'production' }
];

const createEmptyEnvConfig = () => ({
  merchantId: '',
  apiKey: ''
});

const getDefaultConfigByEnvironment = () => ({
  qa: createEmptyEnvConfig(),
  sandbox: createEmptyEnvConfig(),
  production: createEmptyEnvConfig()
});

const isQaEnabled = () => {
  try {
    return String(window.localStorage.getItem('isQA') || '').toLowerCase() === 'true';
  } catch {
    return false;
  }
};

const getEnvironmentOptions = () => {
  return isQaEnabled() ? [{ label: 'QA', value: 'qa' }, ...ENVIRONMENTS] : ENVIRONMENTS;
};

const decodeBase64UrlJson = (encodedValue) => {
  if (!encodedValue) return null;
  try {
    let base64 = String(encodedValue).trim().replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }
    const decoded = atob(base64);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

const getMergedSearchParams = () => {
  const mergedParams = new URLSearchParams(window.location.search || '');
  const hashValue = (typeof window !== 'undefined' && window.location && window.location.hash) ? window.location.hash : '';
  const hashQuery = hashValue.includes('?') ? hashValue.slice(hashValue.indexOf('?') + 1) : '';
  if (hashQuery) {
    const hashParams = new URLSearchParams(hashQuery);
    hashParams.forEach((value, key) => {
      if (!mergedParams.has(key)) {
        mergedParams.append(key, value);
      }
    });
  }
  return mergedParams;
};

const getAuthPayloadFromUrl = () => {
  const params = getMergedSearchParams();
  const rawAuth = String(params.get('auth') || '').trim();
  if (!rawAuth) return null;

  const decoded = decodeBase64UrlJson(rawAuth);
  if (decoded && typeof decoded === 'object') return decoded;

  try {
    const parsed = JSON.parse(rawAuth);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const normalizeEnvironmentValue = (rawEnvironment) => {
  const value = String(rawEnvironment || '').trim().toLowerCase();
  if (!value) return '';
  if (value === 'prod') return 'production';
  if (value === 'sb') return 'sandbox';
  if (value === 'qa' || value === 'sandbox' || value === 'production') {
    return value;
  }
  return '';
};

const getSdkInitParamsFromUrl = () => {
  const params = getMergedSearchParams();
  const environment = normalizeEnvironmentValue(params.get('env') || params.get('environment'));
  const merchantId = String(
    params.get('merchantid') || params.get('merchantId') || params.get('merchant_id') || ''
  ).trim();
  const apiKey = String(
    params.get('apikey') || params.get('apiKey') || params.get('api_key') || ''
  ).trim();

  if (!environment || !merchantId || !apiKey) {
    return null;
  }

  return { environment, merchantId, apiKey };
};

const loadStoredSdkParams = () => {
  try {
    const raw = window.localStorage.getItem(SDK_INIT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const environment = String(parsed.environment || '').trim();
    const merchantId = String(parsed.merchantId || '').trim();
    const apiKey = String(parsed.apiKey || '').trim();
    const allowedEnvironments = getEnvironmentOptions().map((item) => item.value);
    if (!environment || !merchantId || !apiKey || !allowedEnvironments.includes(environment)) {
      return null;
    }
    return { environment, merchantId, apiKey };
  } catch {
    return null;
  }
};

const saveStoredSdkParams = (params) => {
  try {
    window.localStorage.setItem(SDK_INIT_STORAGE_KEY, JSON.stringify(params));
  } catch (error) {
    console.error('Failed to persist SDK params:', error);
  }
};

const loadStoredDashboardModules = () => {
  try {
    const raw = window.localStorage.getItem(DASHBOARD_MODULES_STORAGE_KEY);
    if (!raw) return DEFAULT_DASHBOARD_MODULE_KEYS;

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_DASHBOARD_MODULE_KEYS;

    const allowedKeys = new Set(DEFAULT_DASHBOARD_MODULE_KEYS);
    const sanitized = parsed
      .map((value) => String(value || '').trim().toLowerCase())
      .filter((value) => allowedKeys.has(value));

    return Array.from(new Set(sanitized));
  } catch {
    return DEFAULT_DASHBOARD_MODULE_KEYS;
  }
};

const saveStoredDashboardModules = (modules) => {
  try {
    window.localStorage.setItem(DASHBOARD_MODULES_STORAGE_KEY, JSON.stringify(modules));
  } catch (error) {
    console.error('Failed to persist dashboard modules:', error);
  }
};

const toBase64UrlJson = (payload) => {
  try {
    const json = JSON.stringify(payload || {});
    return btoa(json).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  } catch (_) {
    return '';
  }
};

function App() {
  const hasSuccessfulPaymentRef = useRef(false);
  const autoInitAttemptedRef = useRef(false);
  const activeSdkApiKeyRef = useRef('');
  const hostedPageContextRef = useRef('');
  const fetchAccountStatusRef = useRef(null);
  const availableEnvironments = getEnvironmentOptions();
  const [queryAuthApiKey, setQueryAuthApiKey] = useState('');
  const [environment, setEnvironment] = useState(() => {
    const storedEnv = loadStoredSdkParams()?.environment;
    const allowedEnvironments = getEnvironmentOptions().map((item) => item.value);
    if (storedEnv && allowedEnvironments.includes(storedEnv)) {
      return storedEnv;
    }
    return 'sandbox';
  });
  const [configByEnvironment, setConfigByEnvironment] = useState(() => {
    const defaults = getDefaultConfigByEnvironment();
    const stored = loadStoredSdkParams();
    if (!stored) return defaults;
    return {
      ...defaults,
      [stored.environment]: {
        merchantId: stored.merchantId,
        apiKey: stored.apiKey
      }
    };
  });
  const [isConfigEditable, setIsConfigEditable] = useState(true);

  const [paymentType, setPaymentType] = useState('standard');
  const [amount, setAmount] = useState('1');
  const [currency, setCurrency] = useState('USD');
  const [signingUrl, setSigningUrl] = useState('https://your-server.com/payment-callback');
  const [preAuthTimeSeconds, setPreAuthTimeSeconds] = useState('3600');
  const [recurringExpiry, setRecurringExpiry] = useState(defaultRecurringExpiryIso);
  const [recurringFrequency, setRecurringFrequency] = useState('MONTHLY');
  const [processing, setProcessing] = useState(false);
  const [hostedPageProcessing, setHostedPageProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [sdkInitialized, setSdkInitialized] = useState(false);
  const [sdkInitStatus, setSdkInitStatus] = useState({ type: 'idle', message: '' });
  const [sdkInitInProgress, setSdkInitInProgress] = useState(false);
  const [dashboardModulesPopupOpen, setDashboardModulesPopupOpen] = useState(false);
  const [uuidModalOpen, setUuidModalOpen] = useState(false);
  const [directPaymentEnabled, setDirectPaymentEnabled] = useState(false);
  const [uuidInput, setUuidInput] = useState('');
  const [dashboardModules, setDashboardModules] = useState(() => loadStoredDashboardModules());
  const [accountStatus, setAccountStatus] = useState({
    loading: false,
    linked: false,
    method: 'none',
    error: ''
  });
  const activeEnvironmentConfig = configByEnvironment[environment] || createEmptyEnvConfig();
  const { merchantId, apiKey } = activeEnvironmentConfig;
  const sdkConfigRequirementText = 'Please fill Merchant ID, API Key, and Environment before initializing SDK.';
  const canInitializeSdk =
    merchantId.trim() !== '' &&
    apiKey.trim() !== '' &&
    environment.trim() !== '';

  const updateActiveEnvironmentConfig = useCallback((field, value) => {
    setConfigByEnvironment((prev) => ({
      ...prev,
      [environment]: {
        ...(prev[environment] || createEmptyEnvConfig()),
        [field]: value
      }
    }));
    setSdkInitialized(false);
    setSdkInitStatus({ type: 'idle', message: '' });
  }, [environment]);

  useEffect(() => {
    if (paymentType === 'preauth' && currency !== 'USD') {
      setCurrency('USD');
    }
  }, [paymentType, currency]);

  const formatPaymentError = useCallback((error) => {
    if (error?.details?.response?.errors?.length) {
      return error.details.response.errors.join('. ');
    }
    if (error?.details?.errors?.length) {
      return error.details.errors.join('. ');
    }
    return error?.message || 'An error occurred';
  }, []);

  const normalizeTextMessage = useCallback((value, fallbackText) => {
    const normalized = typeof value === 'string' ? value.trim() : '';
    return normalized || fallbackText;
  }, []);

  const normalizeResultPayload = useCallback((result) => {
    if (result && typeof result === 'object') {
      return result;
    }
    return {};
  }, []);

  const handleAccountChanged = useCallback((result, context = 'ACCOUNT_EVENT') => {
    const safeResult = normalizeResultPayload(result);
    const linked = !!safeResult?.linked;
    const safeRawData = (safeResult?.rawData && typeof safeResult.rawData === 'object') ? safeResult.rawData : {};
    console.log(`[SampleApp] ${context} onAccountChanged`, { linked, rawData: safeRawData });

    setResult({
      type: 'info',
      title: linked ? 'Account Linked' : 'Account Unlinked',
      message: linked
        ? 'Account linked successfully via Web SDK.'
        : 'Account unlinked successfully via Web SDK.',
      data: { linked, rawData: safeRawData }
    });

    if (fetchAccountStatusRef.current) {
      fetchAccountStatusRef.current(true);
    }
  }, [normalizeResultPayload]);

  const handleAccountCancel = useCallback((context = 'ACCOUNT_EVENT') => {
    console.log(`[SampleApp] ${context} onCancel`);
    setResult({
      type: 'info',
      title: 'App Closed',
      message: 'App is closed by user.',
      data: null
    });
  }, []);

  const handleAccountFailed = useCallback((message, result, context = 'ACCOUNT_EVENT') => {
    const safeMessage = normalizeTextMessage(message, ACCOUNT_FAILED_FALLBACK_MESSAGE);
    const safeResult = normalizeResultPayload(result);
    const safeRawData = (safeResult?.rawData && typeof safeResult.rawData === 'object') ? safeResult.rawData : {};
    console.error(`[SampleApp] ${context} onFailed`, { message: safeMessage, rawData: safeRawData });

    setResult({
      type: 'error',
      title: 'Account Request Failed',
      message: safeMessage,
      data: { rawData: safeRawData }
    });
  }, [normalizeResultPayload, normalizeTextMessage]);

  const handlePageFailed = useCallback((message, result, context = 'PAGE_EVENT') => {
    const safeMessage = normalizeTextMessage(message, PAGE_FAILED_FALLBACK_MESSAGE);
    const safeResult = normalizeResultPayload(result);
    const safeRawData = (safeResult?.rawData && typeof safeResult.rawData === 'object') ? safeResult.rawData : {};
    console.error(`[SampleApp] ${context} onPageFailed`, { message: safeMessage, rawData: safeRawData });

    setResult({
      type: 'error',
      title: 'Hosted Page Failed',
      message: safeMessage,
      data: { rawData: safeRawData }
    });
  }, [normalizeResultPayload, normalizeTextMessage]);

  const patchHostedPageAuthPayload = useCallback((params = {}) => {
    try {
      const sdkInstance = typeof Dropp.getInstance === 'function' ? Dropp.getInstance() : null;
      if (!sdkInstance || typeof sdkInstance._getHostedPageAuthToken !== 'function') {
        return;
      }

      const apiKeyForAuth = String(params.apiKey || apiKey || '').trim();
      const merchantAccountForAuth = String(params.merchantId || merchantId || '').trim();
      if (!apiKeyForAuth || !merchantAccountForAuth) {
        return;
      }

      sdkInstance._getHostedPageAuthToken = () => {
        return toBase64UrlJson({
          apikey: apiKeyForAuth,
          apiKey: apiKeyForAuth,
          merchantId: merchantAccountForAuth,
          merchantAccount: merchantAccountForAuth,
          sdkVersion: DROPP_SDK_VERSION,
        });
      };
    } catch (error) {
      console.warn('Unable to patch hosted auth payload:', error);
    }
  }, [apiKey, merchantId]);

  const ensureSdkContextApiKey = useCallback(async (targetApiKey) => {
    const normalizedTargetApiKey = String(targetApiKey || '').trim();
    if (!normalizedTargetApiKey) {
      return;
    }

    if (activeSdkApiKeyRef.current === normalizedTargetApiKey) {
      return;
    }

    const initPayload = {
      merchantId,
      apiKey: normalizedTargetApiKey,
      environment
    };

    await Dropp.init(initPayload);
    patchHostedPageAuthPayload({
      apiKey: normalizedTargetApiKey,
      merchantId,
    });
    activeSdkApiKeyRef.current = normalizedTargetApiKey;
  }, [environment, merchantId, patchHostedPageAuthPayload]);

  const getHostedStatusApiKey = useCallback(() => {
    return String(queryAuthApiKey || apiKey || '').trim();
  }, [apiKey, queryAuthApiKey]);

  const fetchAccountStatus = useCallback(async (forceFetch = false, options = {}) => {
    if (!sdkInitialized && !forceFetch) {
      setAccountStatus({ loading: false, linked: false, method: 'none', error: '' });
      return null;
    }

    const shouldSyncContextApiKey = options?.syncContextApiKey !== false;
    setAccountStatus((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const statusApiKey = getHostedStatusApiKey();
      if (statusApiKey && shouldSyncContextApiKey) {
        await ensureSdkContextApiKey(statusApiKey);
      }

      if (typeof Dropp.status !== 'function') {
        throw new Error('Dropp.status is not available in this SDK version');
      }

      const statusResponse = await Dropp.status();
      const linked = !!statusResponse?.data?.linked;
      const method = linked ? (statusResponse?.data?.method || 'keypair') : 'none';

      setAccountStatus({
        loading: false,
        linked,
        method,
        error: ''
      });
      return statusResponse;
    } catch (error) {
      setAccountStatus((prev) => ({
        ...prev,
        loading: false,
        error: formatPaymentError(error)
      }));
      return null;
    }
  }, [ensureSdkContextApiKey, formatPaymentError, getHostedStatusApiKey, sdkInitialized]);

  useEffect(() => {
    fetchAccountStatusRef.current = fetchAccountStatus;
  }, [fetchAccountStatus]);

  const handleInitializeSdk = useCallback(async () => {
    if (processing || sdkInitInProgress) return;

    if (!canInitializeSdk) {
      setSdkInitialized(false);
      setSdkInitStatus({
        type: 'error',
        message: sdkConfigRequirementText
      });
      setResult({
        type: 'error',
        title: 'Missing SDK Configuration',
        message: sdkConfigRequirementText,
        data: null
      });
      return;
    }

    setSdkInitInProgress(true);
    try {
      const initPayload = {
        merchantId,
        apiKey,
        environment
      };

      await Dropp.init(initPayload);
      patchHostedPageAuthPayload({
        apiKey,
        merchantId,
      });
      activeSdkApiKeyRef.current = apiKey;
      saveStoredSdkParams({
        merchantId,
        apiKey,
        environment
      });
      setSdkInitialized(true);
      setIsConfigEditable(false);
      setSdkInitStatus({
        type: 'success',
        message: 'SDK initialized successfully.'
      });
      await fetchAccountStatus(true);
      setResult({
        type: 'success',
        title: 'SDK Initialized',
        message: 'SDK initialized successfully. You can proceed with payment now.',
        data: null
      });
      console.log('SDK initialized successfully');
    } catch (error) {
      const errorMessage = error?.details?.response?.errors?.length
        ? error.details.response.errors.join('. ')
        : (error?.message || 'SDK initialization failed');
      console.error('SDK initialization failed:', error);
      setSdkInitStatus({ type: 'error', message: errorMessage });
      setResult({
        type: 'error',
        title: 'SDK Initialization Failed',
        message: errorMessage,
        data: null
      });
      setSdkInitialized(false);
    } finally {
      setSdkInitInProgress(false);
    }
  }, [processing, sdkInitInProgress, canInitializeSdk, merchantId, apiKey, environment, fetchAccountStatus, patchHostedPageAuthPayload, sdkConfigRequirementText]);

  useEffect(() => {
    if (autoInitAttemptedRef.current) {
      return;
    }
    autoInitAttemptedRef.current = true;

    const autoInitialize = async () => {
      const urlInitParams = getSdkInitParamsFromUrl();
      const stored = loadStoredSdkParams();
      const initParams = urlInitParams || stored;
      if (!initParams) return;

      if (urlInitParams?.environment === 'qa') {
        try {
          window.localStorage.setItem('isQA', 'true');
        } catch (_) {
          // Ignore localStorage write failures and proceed with initialization.
        }
      }

      setEnvironment(initParams.environment);
      setConfigByEnvironment((prev) => ({
        ...prev,
        [initParams.environment]: {
          merchantId: initParams.merchantId,
          apiKey: initParams.apiKey,
        },
      }));
      setQueryAuthApiKey(initParams.apiKey);

      setSdkInitInProgress(true);
      try {
        const initPayload = {
          merchantId: initParams.merchantId,
          apiKey: initParams.apiKey,
          environment: initParams.environment
        };

        await Dropp.init(initPayload);
        patchHostedPageAuthPayload({
          apiKey: initParams.apiKey,
          merchantId: initParams.merchantId,
        });
        activeSdkApiKeyRef.current = initParams.apiKey;
        saveStoredSdkParams(initParams);
        setSdkInitialized(true);
        setIsConfigEditable(false);
        setSdkInitStatus({
          type: 'success',
          message: urlInitParams
            ? 'SDK auto-initialized from URL parameters.'
            : 'SDK auto-initialized from saved configuration.'
        });
        await fetchAccountStatus(true);
      } catch (error) {
        setSdkInitialized(false);
        setIsConfigEditable(true);
        setSdkInitStatus({
          type: 'error',
          message: `Auto-initialization failed: ${formatPaymentError(error)}`
        });
      } finally {
        setSdkInitInProgress(false);
      }
    };

    autoInitialize();
  }, [fetchAccountStatus, formatPaymentError, patchHostedPageAuthPayload]);

  useEffect(() => {
    const mergedParams = getMergedSearchParams();
    const authPayload = getAuthPayloadFromUrl();
    const directApiKey = String(
      mergedParams.get('apikey') || mergedParams.get('apiKey') || mergedParams.get('api_key') || ''
    ).trim();
    const decodedApiKey = String(
      (authPayload && (authPayload.apikey || authPayload.apiKey)) || ''
    ).trim();

    const apiKeyFromUrl = decodedApiKey || directApiKey;

    if (apiKeyFromUrl) {
      setQueryAuthApiKey(apiKeyFromUrl);
      if (decodedApiKey) {
        console.log('[SampleApp] Decoded auth payload for hosted/status context', {
          hasApiKey: true
        });
      }
      return;
    }

    setQueryAuthApiKey('');
  }, []);

  useEffect(() => {
    const onWindowMessage = (event) => {
      const payload = event && event.data;
      if (!payload || typeof payload !== 'object') {
        return;
      }

      const eventType = String(payload.type || '').trim();
      const eventData = (payload.data && typeof payload.data === 'object') ? payload.data : payload;

      if (eventType === 'ACCOUNT_EVENT') {
        const action = String(eventData.action || '').trim();
        const resultPayload = normalizeResultPayload(eventData.result);

        if (action === 'onAccountChanged') {
          handleAccountChanged(resultPayload, 'ACCOUNT_EVENT');
          return;
        }

        if (action === 'onCancel') {
          handleAccountCancel('ACCOUNT_EVENT');
          return;
        }

        if (action === 'onFailed') {
          handleAccountFailed(eventData.message, resultPayload, 'ACCOUNT_EVENT');
          return;
        }

        console.log('[SampleApp] Ignoring unknown ACCOUNT_EVENT action:', action || '<empty>');
        return;
      }

      if (eventType === 'PAGE_EVENT') {
        const action = String(eventData.action || '').trim();
        const resultPayload = normalizeResultPayload(eventData.result);

        if (action === 'onFailed') {
          handlePageFailed(eventData.message, resultPayload, 'PAGE_EVENT');
          return;
        }

        console.log('[SampleApp] Ignoring unknown PAGE_EVENT action:', action || '<empty>');
      }
    };

    window.addEventListener('message', onWindowMessage);
    return () => {
      window.removeEventListener('message', onWindowMessage);
    };
  }, [handleAccountCancel, handleAccountChanged, handleAccountFailed, handlePageFailed, normalizeResultPayload]);

  const handleChangeConfigs = useCallback(() => {
    if (processing || sdkInitInProgress) return;
    setIsConfigEditable(true);
    setSdkInitialized(false);
    setSdkInitStatus({ type: 'idle', message: '' });
    setResult(null);
  }, [processing, sdkInitInProgress]);

  const getMaskedApiKey = (value) => {
    const sanitizedValue = value.trim();
    if (!sanitizedValue) return '';
    if (sanitizedValue.length <= 4) {
      return '*'.repeat(sanitizedValue.length);
    }
    return `${'*'.repeat(sanitizedValue.length - 4)}${sanitizedValue.slice(-4)}`;
  };

  const buildPaymentOptions = useCallback(() => {
    const amountValue = parseFloat(amount);
    const options = {
      paymentType,
      amount: amountValue,
      currency: currency.trim().toUpperCase(),
      itemName: getItemName(paymentType),
      description: getDescription(paymentType),
      invoiceId: `INV-${paymentType.toUpperCase()}-${Date.now()}`
    };

    if (paymentType === 'preauth') {
      options.authHoldTimeInSeconds = parseInt(preAuthTimeSeconds, 10);
      options.callbackUrl = signingUrl.trim();
    } else if (paymentType === 'recurring') {
      options.callbackUrl = signingUrl.trim();
      options.recurringEndDate = recurringExpiry.trim();
      options.frequency = recurringFrequency;
      options.recurringInterval = recurringFrequency;
    }

    return options;
  }, [
    paymentType,
    amount,
    currency,
    signingUrl,
    preAuthTimeSeconds,
    recurringExpiry,
    recurringFrequency
  ]);

  // Handle payment — field validation is performed by the SDK
  const handlePayment = useCallback(async () => {
    if (!sdkInitialized || processing) return;

    hasSuccessfulPaymentRef.current = false;
    setProcessing(true);
    setResult(null);

    const paymentFields = buildPaymentOptions();
    const options = {
      ...paymentFields,
      onSuccess: (result) => {
        hasSuccessfulPaymentRef.current = true;
        console.log('Payment successful:', result);
        setResult({
          type: 'success',
          title: '✓ Payment Successful',
          message: result.message || 'Transaction completed successfully!',
          data: result
        });
        setProcessing(false);
      },
      onFailure: (result) => {
        console.error('Payment failed:', result);
        setResult({
          type: 'error',
          title: '✗ Payment Failed',
          message: result.message || 'Payment transaction failed',
          data: result
        });
        setProcessing(false);
      },
      onCancel: (result) => {
        if (hasSuccessfulPaymentRef.current) {
          console.log('Payment close received after success, ignoring cancel callback:', result);
          setProcessing(false);
          return;
        }
        console.log('Payment cancelled:', result);
        setResult({
          type: 'info',
          title: 'Payment Cancelled',
          message: `Payment was cancelled. ${result.reason ? `Reason: ${result.reason}` : ''}`,
          data: result
        });
        setProcessing(false);
      },
      onClose: () => {
        console.log('Modal closed');
        setProcessing(false);
      }
    };

    console.log('Initiating payment:', paymentFields);

    try {
      await ensureSdkContextApiKey(apiKey);
      await Dropp.pay(options);
    } catch (error) {
      console.error('Payment error:', error);
      const isValidationError = error?.code === 'INVALID_CONFIG';
      setResult({
        type: 'error',
        title: isValidationError ? 'Invalid Payment Configuration' : 'Payment Error',
        message: formatPaymentError(error),
        data: error
      });
      setProcessing(false);
    }
  }, [apiKey, buildPaymentOptions, ensureSdkContextApiKey, sdkInitialized, processing, formatPaymentError]);

  const handleOpenHostedPage = useCallback(async (pageType, hostedPageOptions = {}) => {
    if (!sdkInitialized || processing || hostedPageProcessing) return;

    setHostedPageProcessing(true);
    setResult(null);
    hostedPageContextRef.current = pageType;

    const hostedPageCallbacks = {
      onAccountChanged: (accountResult) => {
        handleAccountChanged(accountResult, `${pageType}:callback`);
      },
      onCancel: () => {
        handleAccountCancel(`${pageType}:callback`);
      },
      onFailed: (message, accountResult) => {
        handleAccountFailed(message, accountResult, `${pageType}:callback`);
      },
      onPageFailed: (message, pageResult) => {
        handlePageFailed(message, pageResult, `${pageType}:callback`);
      }
    };

    const onClose = async () => {
      console.log(`${pageType} modal closed`);
      await fetchAccountStatus();
      setHostedPageProcessing(false);
    };

    try {
      const hostedApiKey = getHostedStatusApiKey();
      if (hostedApiKey) {
        await ensureSdkContextApiKey(hostedApiKey);
      }

      switch (pageType) {
        case 'dashboard':
          if (typeof Dropp.dashboard !== 'function') {
            throw new Error('Dropp.dashboard is not available in this SDK version');
          }
          await Dropp.dashboard({
            onClose,
            ...hostedPageCallbacks,
            ...(Array.isArray(hostedPageOptions.modules) ? { modules: hostedPageOptions.modules } : {})
          });
          break;
        case 'unlink':
          if (typeof Dropp.unlinkAccount !== 'function') {
            throw new Error('Dropp.unlinkAccount is not available in this SDK version');
          }
          await Dropp.unlinkAccount({ onClose, ...hostedPageCallbacks });
          break;
        case 'transactions':
          if (typeof Dropp.transactions !== 'function') {
            throw new Error('Dropp.transactions is not available in this SDK version');
          }
          await Dropp.transactions({ onClose, ...hostedPageCallbacks });
          break;
        case 'offers':
          if (typeof Dropp.offers !== 'function') {
            throw new Error('Dropp.offers is not available in this SDK version');
          }
          await Dropp.offers({ onClose, ...hostedPageCallbacks });
          break;
        default:
          throw new Error('Unsupported hosted page action');
      }
    } catch (error) {
      console.error('Hosted page open failed:', error);
      setResult({
        type: 'error',
        title: 'Hosted Page Error',
        message: formatPaymentError(error),
        data: error
      });
    } finally {
      hostedPageContextRef.current = '';
      setHostedPageProcessing(false);
    }
  }, [ensureSdkContextApiKey, fetchAccountStatus, formatPaymentError, getHostedStatusApiKey, handleAccountCancel, handleAccountChanged, handleAccountFailed, handlePageFailed, hostedPageProcessing, processing, sdkInitialized]);

  const handleOpenModule = useCallback(async (moduleKey) => {
    if (!sdkInitialized || processing || hostedPageProcessing) return;

    setHostedPageProcessing(true);
    setResult(null);
    hostedPageContextRef.current = moduleKey;

    const moduleCallbacks = {
      onClose: async () => {
        console.log(`${moduleKey} modal closed`);
        await fetchAccountStatus();
        setHostedPageProcessing(false);
      },
      onAccountChanged: (accountResult) => {
        handleAccountChanged(accountResult, `${moduleKey}:callback`);
      },
      onCancel: () => {
        handleAccountCancel(`${moduleKey}:callback`);
      },
      onFailed: (message, accountResult) => {
        handleAccountFailed(message, accountResult, `${moduleKey}:callback`);
      },
      onPageFailed: (message, pageResult) => {
        handlePageFailed(message, pageResult, `${moduleKey}:callback`);
      }
    };

    try {
      const hostedApiKey = getHostedStatusApiKey();
      if (hostedApiKey) {
        await ensureSdkContextApiKey(hostedApiKey);
      }

      if (typeof Dropp.open !== 'function') {
        throw new Error('Dropp.open is not available in this SDK version');
      }

      await Dropp.open(moduleKey, moduleCallbacks);
      await fetchAccountStatus();
    } catch (error) {
      console.error('Module open failed:', error);
      setResult({
        type: 'error',
        title: 'Module Open Error',
        message: formatPaymentError(error),
        data: error
      });
    } finally {
      hostedPageContextRef.current = '';
      setHostedPageProcessing(false);
    }
  }, [ensureSdkContextApiKey, fetchAccountStatus, formatPaymentError, getHostedStatusApiKey, handleAccountCancel, handleAccountChanged, handleAccountFailed, handlePageFailed, hostedPageProcessing, processing, sdkInitialized]);

  const initiateStandardPaymentFromUuidResult = useCallback(async (uuidResult, source) => {
    const parseMaybeJson = (value) => {
      if (typeof value !== 'string') {
        return value;
      }

      try {
        return JSON.parse(value);
      } catch (_) {
        return null;
      }
    };

    const topLevelData = parseMaybeJson(uuidResult?.data);
    const rawResponseData = parseMaybeJson(uuidResult?.rawResponse?.data);
    const candidates = [
      topLevelData?.data,
      topLevelData,
      rawResponseData?.data,
      rawResponseData,
    ];
    const paymentDetails = candidates.find((candidate) => candidate && typeof candidate === 'object') || null;

    const amountValue = Number(paymentDetails?.amount);
    const currencyValue = String(paymentDetails?.currency || '').trim().toUpperCase();
    const descriptionValue = String(paymentDetails?.description || '').trim();
    const itemNameValue = String(paymentDetails?.title || 'QR Payment').trim() || 'QR Payment';

    if (!Number.isFinite(amountValue) || amountValue <= 0 || !currencyValue || !descriptionValue) {
      setResult({
        type: 'info',
        title: 'UUID Data fetched',
        message: 'UUID data was fetched, but payment was not initiated because amount/currency/description is incomplete.',
        data: uuidResult || null,
      });
      return;
    }

    hasSuccessfulPaymentRef.current = false;
    setPaymentType('standard');
    setAmount(String(amountValue));
    setCurrency(currencyValue);
    setProcessing(true);
    setResult({
      type: 'info',
      title: 'Initiating Payment',
      message: `Starting standard payment from ${source} result.`,
      data: {
        amount: amountValue,
        currency: currencyValue,
        description: descriptionValue,
        title: itemNameValue,
      },
    });

    try {
      await Dropp.pay({
        paymentType: 'standard',
        amount: amountValue,
        currency: currencyValue,
        itemName: itemNameValue,
        description: descriptionValue,
        invoiceId: `INV-UUID-${Date.now()}`,
        onSuccess: (result) => {
          hasSuccessfulPaymentRef.current = true;
          setResult({
            type: 'success',
            title: '✓ Payment Successful',
            message: result?.message || 'Transaction completed successfully!',
            data: result || null,
          });
          setProcessing(false);
        },
        onFailure: (result) => {
          setResult({
            type: 'error',
            title: '✗ Payment Failed',
            message: result?.message || 'Payment transaction failed',
            data: result || null,
          });
          setProcessing(false);
        },
        onCancel: (result) => {
          if (hasSuccessfulPaymentRef.current) {
            setProcessing(false);
            return;
          }
          setResult({
            type: 'info',
            title: 'Payment Cancelled',
            message: `Payment was cancelled. ${result?.reason ? `Reason: ${result.reason}` : ''}`,
            data: result || null,
          });
          setProcessing(false);
        },
        onClose: () => {
          setProcessing(false);
        },
      });
    } catch (error) {
      setResult({
        type: 'error',
        title: 'Payment Error',
        message: formatPaymentError(error),
        data: error,
      });
      setProcessing(false);
    }
  }, [formatPaymentError]);

  const handleScanPaymentDetails = useCallback(async () => {
    if (!sdkInitialized || processing || hostedPageProcessing) return;

    setHostedPageProcessing(true);
    setResult(null);

    try {
      const hostedApiKey = getHostedStatusApiKey();
      if (hostedApiKey) {
        await ensureSdkContextApiKey(hostedApiKey);
      }

      if (typeof Dropp.scanPaymentDetails !== 'function') {
        throw new Error('Dropp.scanPaymentDetails is not available in this SDK version');
      }

      const sdkResponse = await Dropp.scanPaymentDetails();
      const success = !!sdkResponse?.success;

      setResult({
        type: success ? 'success' : 'error',
        title: success ? 'UUID Data fetched' : 'No data found or invalid UUID',
        message: sdkResponse?.message || (success ? 'Payment details fetched successfully.' : 'Unable to fetch payment details.'),
        data: sdkResponse || null,
      });

      if (success && directPaymentEnabled) {
        setHostedPageProcessing(false);
        await initiateStandardPaymentFromUuidResult(sdkResponse, 'scan');
      }
    } catch (error) {
      console.error('Scan payment details failed:', error);
      setResult({
        type: 'error',
        title: 'Scan Payment Details Error',
        message: formatPaymentError(error),
        data: error,
      });
    } finally {
      setHostedPageProcessing(false);
    }
  }, [directPaymentEnabled, ensureSdkContextApiKey, formatPaymentError, getHostedStatusApiKey, hostedPageProcessing, initiateStandardPaymentFromUuidResult, processing, sdkInitialized]);

  const handleFetchPaymentDetails = useCallback(async (uuidValue) => {
    const normalizedUuid = String(uuidValue || '').trim();

    if (!normalizedUuid) {
      setResult({
        type: 'error',
        title: 'Missing UUID',
        message: 'Please enter a UUID.',
        data: null,
      });
      return;
    }

    if (!UUID_REGEX.test(normalizedUuid)) {
      setResult({
        type: 'error',
        title: 'Invalid UUID',
        message: 'Please enter a valid UUID format.',
        data: { uuid: normalizedUuid },
      });
      return;
    }

    if (!sdkInitialized || processing || hostedPageProcessing) return;

    setHostedPageProcessing(true);
    setResult(null);

    try {
      const hostedApiKey = getHostedStatusApiKey();
      if (hostedApiKey) {
        await ensureSdkContextApiKey(hostedApiKey);
      }

      if (typeof Dropp.fetchPaymentDetails !== 'function') {
        throw new Error('Dropp.fetchPaymentDetails is not available in this SDK version');
      }

      const sdkResponse = await Dropp.fetchPaymentDetails(normalizedUuid);
      const success = !!sdkResponse?.success;

      setResult({
        type: success ? 'success' : 'error',
        title: success ? 'UUID Data fetched' : 'No data found or invalid UUID',
        message: sdkResponse?.message || (success ? 'Payment details fetched successfully.' : 'Unable to fetch payment details.'),
        data: sdkResponse || null,
      });

      if (success && directPaymentEnabled) {
        setUuidModalOpen(false);
        setHostedPageProcessing(false);
        await initiateStandardPaymentFromUuidResult(sdkResponse, 'uuid lookup');
      }
    } catch (error) {
      console.error('Fetch payment details failed:', error);
      setResult({
        type: 'error',
        title: 'Fetch Payment Details Error',
        message: formatPaymentError(error),
        data: error,
      });
    } finally {
      setHostedPageProcessing(false);
      setUuidModalOpen(false);
    }
  }, [directPaymentEnabled, ensureSdkContextApiKey, formatPaymentError, getHostedStatusApiKey, hostedPageProcessing, initiateStandardPaymentFromUuidResult, processing, sdkInitialized]);

  const toggleDashboardModule = useCallback((moduleKey) => {
    setDashboardModules((prev) => {
      const nextSet = new Set(prev);
      const sectionItems = DASHBOARD_SECTION_ITEMS_MAP[moduleKey];

      if (sectionItems) {
        const sectionSelected = nextSet.has(moduleKey);
        if (sectionSelected) {
          nextSet.delete(moduleKey);
          sectionItems.forEach((itemKey) => nextSet.delete(itemKey));
        } else {
          nextSet.add(moduleKey);
          sectionItems.forEach((itemKey) => nextSet.add(itemKey));
        }
      } else if (nextSet.has(moduleKey)) {
        nextSet.delete(moduleKey);
      } else {
        nextSet.add(moduleKey);
      }

      const nextModules = DEFAULT_DASHBOARD_MODULE_KEYS.filter((key) => nextSet.has(key));
      saveStoredDashboardModules(nextModules);
      return nextModules;
    });
  }, []);

  // const handleOpenHostedPage = useCallback(async (pageType) => {
  //   if (!sdkInitialized || processing || hostedPageProcessing) return;

  //   setHostedPageProcessing(true);
  //   setResult(null);

  //   const onClose = () => {
  //     console.log(`${pageType} modal closed`);
  //     setHostedPageProcessing(false);
  //   };

  //   try {
  //     switch (pageType) {
  //       case 'dashboard':
  //         if (typeof Dropp.dashboard !== 'function') {
  //           throw new Error('Dropp.dashboard is not available in this SDK version');
  //         }
  //         await Dropp.dashboard({ onClose });
  //         break;
  //       case 'unlink':
  //         if (typeof Dropp.unlinkAccount !== 'function') {
  //           throw new Error('Dropp.unlinkAccount is not available in this SDK version');
  //         }
  //         await Dropp.unlinkAccount({ onClose });
  //         break;
  //       case 'transactions':
  //         if (typeof Dropp.transactions !== 'function') {
  //           throw new Error('Dropp.transactions is not available in this SDK version');
  //         }
  //         await Dropp.transactions({ onClose });
  //         break;
  //       case 'offers':
  //         if (typeof Dropp.offers !== 'function') {
  //           throw new Error('Dropp.offers is not available in this SDK version');
  //         }
  //         await Dropp.offers({ onClose });
  //         break;
  //       default:
  //         throw new Error('Unsupported hosted page action');
  //     }
  //   } catch (error) {
  //     console.error('Hosted page open failed:', error);
  //     setResult({
  //       type: 'error',
  //       title: 'Hosted Page Error',
  //       message: formatPaymentError(error),
  //       data: error
  //     });
  //   } finally {
  //     setHostedPageProcessing(false);
  //   }
  // }, [formatPaymentError, hostedPageProcessing, processing, sdkInitialized]);

  // Helper functions
  const getItemName = (paymentType) => {
    switch (paymentType) {
      case 'preauth':
        return 'Hotel Reservation - Pre-Authorization';
      case 'recurring':
        return 'Monthly Subscription';
      default:
        return 'Test Product Purchase';
    }
  };

  const getDescription = (paymentType) => {
    switch (paymentType) {
      case 'preauth':
        return 'Authorization hold for booking confirmation';
      case 'recurring':
        return 'Recurring monthly payment';
      default:
        return 'Standard payment transaction';
    }
  };

  const isLinked = !!accountStatus.linked;
  const canUseSdkFeatures = sdkInitialized && isLinked;
  const visibleOpenModuleKeys = DASHBOARD_ITEM_MODULE_KEYS.filter((itemKey) => dashboardModules.includes(itemKey));

  return (
    <div className="app">
      <div className="container">
            {/* Header */}
            <div className="header">
              <img
                className="header-logo"
                src="https://dropp.cc/wp-content/uploads/2021/07/logo-dropp@2x.png"
                alt="Dropp Logo"
              />
              <h1>Dropp Web SDK</h1>
              <p>Dropp Web SDK Sample app</p>
            </div>
        <div className="layout-desktop-split">
          <div className="layout-column">

            {/* Configuration Card */}
            <div className="card">
              <div className="section-title-row">
                <h3>SDK Configuration</h3>
                <span
                  className="info-tooltip"
                  aria-label="SDK configuration info"
                  title="The configuration parameters you will get from merchant dashboard after registration and completing your KYC Process."
                >
                  i
                  <span className="tooltip-text" role="tooltip">
                    The configuration parameters you will get from merchant dashboard after registration and completing your KYC Process.
                  </span>
                </span>
              </div>

          {isConfigEditable ? (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="environment">Environment</label>
                  <select
                    id="environment"
                    value={environment}
                    onChange={(e) => {
                      setEnvironment(e.target.value);
                      setSdkInitialized(false);
                      setSdkInitStatus({ type: 'idle', message: '' });
                    }}
                    disabled={processing}
                  >
                    {availableEnvironments.map(({ label, value }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">

                <div className="form-group">
                  <label htmlFor="merchantId">Merchant ID</label>
                  <input
                    type="text"
                    id="merchantId"
                    value={merchantId}
                    onChange={(e) => updateActiveEnvironmentConfig('merchantId', e.target.value)}
                    placeholder="0.0.123456"
                    disabled={processing}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="apiKey">API Key</label>
                  <input
                    type="text"
                    id="apiKey"
                    value={apiKey}
                    onChange={(e) => updateActiveEnvironmentConfig('apiKey', e.target.value)}
                    placeholder="4b3a6dd3e..................."
                    disabled={processing}
                  />
                </div>
              </div>

              <div className="button-row button-row-single">
                <button
                  className="button button-secondary button-compact"
                  onClick={handleInitializeSdk}
                  disabled={processing || sdkInitInProgress || !canInitializeSdk}
                >
                  <span className="icon">⚙️</span>
                  <span>{sdkInitInProgress ? 'Initializing SDK...' : 'Initialize SDK'}</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="config-readonly-card">
                <div className="config-readonly-row">
                  <span className="config-key">Environment</span>
                  <span className="config-value">
                    {availableEnvironments.find((env) => env.value === environment)?.label || environment}
                  </span>
                </div>
                <div className="config-readonly-row">
                  <span className="config-key">Merchant ID</span>
                  <span className="config-value">{merchantId}</span>
                </div>
                <div className="config-readonly-row">
                  <span className="config-key">API Key</span>
                  <span className="config-value">{getMaskedApiKey(apiKey)}</span>
                </div>
              </div>

              <div className="button-row">
                <button
                  className="button button-tertiary button-compact"
                  onClick={handleChangeConfigs}
                  disabled={processing || sdkInitInProgress}
                >
                  <span>Change Configs</span>
                </button>
                <button
                  className="button button-secondary button-compact"
                  onClick={handleInitializeSdk}
                  disabled={processing || sdkInitInProgress || !canInitializeSdk}
                >
                  <span>{sdkInitInProgress ? 'Reinitializing...' : 'Reinitialize'}</span>
                </button>
              </div>
            </>
          )}

              {sdkInitStatus.type !== 'idle' && (
                <div className={`sdk-init-status sdk-init-status-${sdkInitStatus.type}`}>
                  {sdkInitStatus.type === 'success' ? 'SDK Initialized: ' : 'Initialization Error: '}
                  {sdkInitStatus.message}
                </div>
              )}
            </div>


            {/* Payment Configuration Card */}
            <div className="card">
              <h3>Payment Configuration</h3>

          <fieldset className="payment-type-fieldset" disabled={processing}>
            <legend className="payment-type-legend">Payment Type</legend>
            <div className="radio-row">
              <label className="radio-option">
                <input
                  type="radio"
                  name="paymentType"
                  value="standard"
                  checked={paymentType === 'standard'}
                  onChange={(e) => setPaymentType(e.target.value)}
                />
                <span>Standard</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="paymentType"
                  value="preauth"
                  checked={paymentType === 'preauth'}
                  onChange={(e) => setPaymentType(e.target.value)}
                />
                <span>Preauth</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="paymentType"
                  value="recurring"
                  checked={paymentType === 'recurring'}
                  onChange={(e) => setPaymentType(e.target.value)}
                />
                <span>Recurring</span>
              </label>
            </div>
          </fieldset>

          <div
            className={`form-row${
              paymentType === 'recurring' || paymentType === 'preauth'
                ? ' form-row-payment-fields'
                : ''
            }`}
          >
            <div className="form-group form-group-narrow">
              <label htmlFor="currency">Currency</label>
              <select
                id="currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                disabled={processing}
              >
                <option value="USD">USD</option>
                {paymentType !== 'preauth' && <option value="HBAR">HBAR</option>}
                {paymentType !== 'preauth' && <option value="USDC">USDC</option>}
              </select>
            </div>

            <div className="form-group form-group-narrow">
              <label htmlFor="amount">Amount</label>
              <input
                type="number"
                id="amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                step="0.01"
                min="0.01"
                disabled={processing}
              />
            </div>

            {paymentType === 'preauth' && (
              <div className="form-group form-group-wide">
                <label htmlFor="preAuthTimeSeconds">Pre-auth Time (Seconds)</label>
                <input
                  type="number"
                  id="preAuthTimeSeconds"
                  value={preAuthTimeSeconds}
                  onChange={(e) => setPreAuthTimeSeconds(e.target.value)}
                  min="1"
                  step="1"
                  disabled={processing}
                />
              </div>
            )}

            {paymentType === 'recurring' && (
              <div className="form-group form-group-wide">
                <label htmlFor="recurringExpiry">Expiry</label>
                <input
                  type="datetime-local"
                  id="recurringExpiry"
                  value={toDatetimeLocalValue(recurringExpiry)}
                  onChange={(e) =>
                    setRecurringExpiry(fromDatetimeLocalValue(e.target.value))
                  }
                  disabled={processing}
                />
              </div>
            )}
          </div>

          {paymentType === 'recurring' && (
            <div className="form-row form-row-recurring-extra">
              <div className="form-group form-group-medium">
                <label htmlFor="recurringFrequency">Frequency</label>
                <select
                  id="recurringFrequency"
                  value={recurringFrequency}
                  onChange={(e) => setRecurringFrequency(e.target.value)}
                  disabled={processing}
                >
                  {RECURRING_FREQUENCIES.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {paymentType === 'preauth' && (
            <div className="form-group">
              <label htmlFor="signingUrl">Signing URL</label>
              <input
                type="url"
                id="signingUrl"
                value={signingUrl}
                onChange={(e) => setSigningUrl(e.target.value)}
                placeholder="https://your-server.com/pre-auth-post-callback"
                disabled={processing}
              />
            </div>
          )}

          {paymentType === 'recurring' && (
            <div className="form-group">
              <label htmlFor="signingUrl">Signing URL</label>
              <input
                type="url"
                id="signingUrl"
                value={signingUrl}
                onChange={(e) => setSigningUrl(e.target.value)}
                placeholder="https://your-server.com/rps-callback"
                disabled={processing}
              />
            </div>
          )}

              <div className="button-row button-row-single">
                <button
                  className="button button-primary button-compact"
                  onClick={handlePayment}
                  disabled={processing || !canUseSdkFeatures}
                >
                  <span className="icon">💳</span>
                  <span>{processing ? 'Processing...' : 'Proceed'}</span>
                </button>
              </div>
            </div>

            <div className="card">
              <h3>UUID Payment</h3>

              <div className="uuid-payment-toggle-row">
                <label className="uuid-payment-toggle" htmlFor="directPaymentToggle">
                  <input
                    id="directPaymentToggle"
                    type="checkbox"
                    checked={directPaymentEnabled}
                    onChange={(event) => setDirectPaymentEnabled(event.target.checked)}
                    disabled={processing || hostedPageProcessing || !canUseSdkFeatures}
                  />
                  <span>Direct Payment</span>
                </label>
                <span className="uuid-payment-toggle-hint">
                  {directPaymentEnabled
                    ? 'Enabled: success response auto-starts payment.'
                    : 'Disabled: success response is shown without starting payment.'}
                </span>
              </div>

              <div className="button-row">
                <button
                  className="button button-secondary button-compact"
                  onClick={handleScanPaymentDetails}
                  disabled={processing || hostedPageProcessing || !canUseSdkFeatures}
                >
                  <span>{hostedPageProcessing ? 'Opening...' : 'Scan a QR'}</span>
                </button>
                <button
                  className="button button-tertiary button-compact"
                  onClick={() => setUuidModalOpen(true)}
                  disabled={processing || hostedPageProcessing || !canUseSdkFeatures}
                >
                  <span>I have UUID</span>
                </button>
              </div>
            </div>
          </div>

          <div className="layout-column">

            {/* Hosted Pages Card */}
            <div className="card">
              <h3>Other Features</h3>

          <div className="result info" style={{ marginBottom: 16 }}>
            <div className="result-title">Account Status</div>
            <div className="result-message">
              {sdkInitialized
                ? `Linked: ${String(!!accountStatus.linked)} | Method: ${accountStatus.linked ? accountStatus.method : 'none'}`
                : 'Initialize SDK to fetch account status.'}
            </div>
            {accountStatus.error && (
              <div className="result-message" style={{ color: '#b71c1c' }}>
                {accountStatus.error}
              </div>
            )}
            <div className="button-row button-row-single" style={{ marginTop: 10 }}>
              <button
                className="button button-secondary button-compact"
                onClick={() => fetchAccountStatus(false, { syncContextApiKey: false })}
                disabled={!sdkInitialized || accountStatus.loading || processing || hostedPageProcessing}
              >
                <span>{accountStatus.loading ? 'Refreshing...' : 'Refresh Status'}</span>
              </button>
                <button
                  className="button button-danger button-compact modules-dropdown-button"
                  onClick={() => setDashboardModulesPopupOpen(true)}
                  disabled={processing || hostedPageProcessing || !canUseSdkFeatures}
                  title="Select modules to pass into Dropp.dashboard"
                >
                  <span>Select Modules ▼</span>
                </button>
            </div>
          </div>

          {!isLinked && (
            <div className="button-row button-row-single">
              <button
                className="button button-primary button-compact"
                onClick={() => handleOpenHostedPage('dashboard')}
                disabled={processing || hostedPageProcessing || !sdkInitialized}
              >
                <span>{hostedPageProcessing ? 'Opening...' : 'Link Account'}</span>
              </button>
            </div>
          )}

          {isLinked && (
            <>
              <div className="button-row">
                <button
                  className="button button-primary button-compact"
                  onClick={() => handleOpenHostedPage('dashboard', { modules: dashboardModules })}
                  disabled={processing || hostedPageProcessing || !canUseSdkFeatures}
                >
                  <span>Home/Dashboard</span>
                </button>
                <button
                  className="button button-tertiary button-compact"
                  onClick={() => handleOpenHostedPage('unlink')}
                  disabled={processing || hostedPageProcessing || !canUseSdkFeatures}
                >
                  <span>Unlink Account</span>
                </button>
              </div>

              <div className="open-modules-section">
                <h4>Open Modules Directly (Dropp.open)</h4>
                <div className="open-modules-grid">
                  {visibleOpenModuleKeys.map((moduleKey) => (
                    <button
                      key={moduleKey}
                      className="button button-secondary button-compact"
                      onClick={() => handleOpenModule(moduleKey)}
                      disabled={processing || hostedPageProcessing || !canUseSdkFeatures}
                      title={`Open module: ${moduleKey}`}
                    >
                      <span>{DASHBOARD_MODULE_LABELS[moduleKey] || moduleKey}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
            </div>


            {/* Result Card */}
            {result && (
              <div className={`result ${result.type}`}>
                <div className="result-title">{result.title}</div>
                <div className="result-message">{result.message}</div>
                {result.data && (
                  <details className="result-details">
                    <summary>View Details</summary>
                    <pre>{JSON.stringify(result.data, null, 2)}</pre>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>

        {dashboardModulesPopupOpen && (
          <div className="modules-popup-backdrop" onClick={() => setDashboardModulesPopupOpen(false)}>
            <div className="modules-popup" onClick={(event) => event.stopPropagation()}>
              <div className="modules-popup-header">
                <h4>Dashboard Modules</h4>
                <button className="modules-popup-close" onClick={() => setDashboardModulesPopupOpen(false)}>×</button>
              </div>
              <p className="modules-popup-subtitle">
                Select modules to pass in <strong>Dropp.dashboard({`{ modules }`})</strong>.
              </p>
              <p className="modules-popup-subtitle modules-popup-subtitle-secondary">
                Select a section for complete section availability. Select individual items for item-level access only.
              </p>

              <div className="modules-popup-list">
                {DASHBOARD_SECTION_DEFINITIONS.map((section) => {
                  const sectionChecked = dashboardModules.includes(section.key);
                  return (
                    <div key={section.key} className="modules-popup-section-card">
                      <label className="modules-popup-item modules-popup-item-section">
                        <input
                          type="checkbox"
                          checked={sectionChecked}
                          onChange={() => toggleDashboardModule(section.key)}
                        />
                        <span className="modules-popup-item-label">{section.label}</span>
                        <span className="modules-popup-item-meta">Section · {section.key}</span>
                      </label>
                      <div className="modules-popup-section-items">
                        {section.items.map((itemKey) => {
                          const checked = dashboardModules.includes(itemKey);
                          return (
                            <label key={itemKey} className="modules-popup-item modules-popup-item-module">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleDashboardModule(itemKey)}
                              />
                              <span className="modules-popup-item-label">{DASHBOARD_MODULE_LABELS[itemKey] || itemKey}</span>
                              <span className="modules-popup-item-meta">Module · {itemKey}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {uuidModalOpen && (
          <div className="modules-popup-backdrop" onClick={() => setUuidModalOpen(false)}>
            <div className="uuid-popup" onClick={(event) => event.stopPropagation()}>
              <div className="modules-popup-header">
                <h4>Fetch Payment Details</h4>
                <button className="modules-popup-close" onClick={() => setUuidModalOpen(false)}>×</button>
              </div>
              <div className="uuid-popup-content">
                <label htmlFor="payment-uuid-input" className="uuid-popup-label">UUID</label>
                <input
                  id="payment-uuid-input"
                  className="uuid-popup-input"
                  type="text"
                  placeholder="6b0f8bb2-6d17-4d5d-bb5f-478e37dcae11"
                  value={uuidInput}
                  onChange={(event) => setUuidInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleFetchPaymentDetails(uuidInput);
                    }
                  }}
                />
                <div className="button-row">
                  <button
                    className="button button-secondary button-compact"
                    onClick={() => handleFetchPaymentDetails(uuidInput)}
                    disabled={processing || hostedPageProcessing || !sdkInitialized}
                  >
                    <span>{hostedPageProcessing ? 'Fetching...' : 'Fetch Details'}</span>
                  </button>
                  <button
                    className="button button-danger button-compact"
                    onClick={() => setUuidModalOpen(false)}
                    disabled={hostedPageProcessing}
                  >
                    <span>Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="footer">
        {`Dropp Payment SDK v${DROPP_SDK_VERSION} | Dropp Payment SDK Sample Application`}
      </div>
    </div>
  );
}

export default App;
